import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MODELS from '../../../../constants/models';
import ROLES from '../../../../constants/roles';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import { getStage } from '../../../../services/allInvoiceApi';
import { getCountries } from '../../../../services/addMemberApi';
import { getTagData } from '../../../../services/tagApi';
import { CustomFilterBox, FilterTextField } from './utils';

const CustomFilter = forwardRef(function CustomFilter(props, ref) {
  const { FilterType, gridKey, setFilterDropdownList, filterDropDownList } =
    props;
  const [dropDownList, setDropDownList] = useState([]);
  const [textFieldFilter, setTextFieldFilter] = useState({});
  const [dateFilter, setDateFilter] = useState({});
  const loginRole = getLocalStorageItem('role');
  const superAdminOrSuperManager =
    loginRole === ROLES.SUPER_ADMIN || loginRole === ROLES.SUPER_MANAGER;
  const getTags = async () => {
    try {
      const res = await getTagData();
      if (res) {
        const updatedResponse = res.map((r) => ({
          ...r,
          label: r.name,
          isCheck: false,
        }));
        return updatedResponse;
      }
    } catch (err) {
      return err.message;
    }
  };
  const status = async () => {
    try {
      const res = await getStage();
      if (res) {
        const updatedResponse = res.map((r) => ({
          ...r,
          label: r.status,
          value: r.status,
          isCheck: false,
        }));
        const updatedResponseForNotification = res
          .map((r) => ({ ...r, label: r.status, isCheck: false }))
          .filter((f) => f.status !== 'Inprogress' && f.status !== 'Pending');
        if (gridKey === 'notifications') {
          return updatedResponseForNotification;
        }
        return updatedResponse;
      }
    } catch (err) {
      return err.message;
    }
  };

  const countries = async () => {
    try {
      const res = await getCountries();
      if (res) {
        const updatedResponse = res.map((r) => ({
          ...r,
          label: r.country_name,
          isCheck: false,
        }));
        return updatedResponse;
      }
    } catch (err) {
      return err.message;
    }
  };
  const handleClearCurrentTabValue = (latestFilterItem) => {
    if (FilterType && FilterType?.type === 'checkBox') {
      if (
        Object.keys(latestFilterItem).length > 0 &&
        latestFilterItem[FilterType?.field]?.length > 0 &&
        dropDownList.length > 0
      ) {
        const updatedData = dropDownList.map((item) => {
          if (
            !latestFilterItem[FilterType?.field]?.some(
              (ele) => ele.label === item.label,
            ) &&
            item.isCheck
          ) {
            return { ...item, isCheck: false }; // Update isCheck to true
          }
          return item; // Return the item as is if no update needed
        });
        setDropDownList(updatedData);
      } else {
        setDropDownList(
          dropDownList.map((elem) => ({
            ...elem,
            isCheck: false,
          })),
        );
      }
    }
    if (FilterType && FilterType?.type === 'text') {
      if (!Object.hasOwn(latestFilterItem, FilterType?.field)) {
        setTextFieldFilter({
          filter: '',
          filterType: 'text',
        });
      }
    }
    if (FilterType && FilterType?.type === 'date') {
      if (!Object.hasOwn(latestFilterItem, FilterType?.field)) {
        setDateFilter({
          dateFrom: null,
          dateTo: null,
          filterType: 'date',
          type: 'inRange',
        });
      }
    }
  };
  useImperativeHandle(ref, () => ({
    callChildFunction: handleClearCurrentTabValue,
  }));
  const isValueInArray = (array, searchValue) => {
    let result = false;
    if (array.length && searchValue) {
      result = array.some((obj) => Object.values(obj).includes(searchValue));
    }
    return result;
  };
  const getDropDownList = async () => {
    const activeStatus = [];
    if (FilterType?.field === 'associatedTags') {
      const data = await getTags();
      activeStatus.push(...data);
    } else if (FilterType?.field === 'country_name') {
      const data = await countries();
      activeStatus.push(...data);
    } else if (
      FilterType?.field === 'status' ||
      FilterType?.field === 'document_status'
    ) {
      const data = await status();
      activeStatus.push(...data);
    } else if (FilterType?.field === 'is_active') {
      activeStatus.push(
        { label: 'Active', value: 'isActive', isCheck: false },
        { label: 'InActive', value: 'isInactive', isCheck: false },
      );
    } else if (FilterType?.field === 'notification_status') {
      activeStatus.push(
        { label: 'read', value: 'read', isCheck: false },
        { label: 'unread', value: 'unread', isCheck: false },
      );
    } else if (FilterType?.field === 'role') {
      const roleStatus = [];
      if (gridKey === 'activity-history') {
        if (superAdminOrSuperManager) {
          roleStatus.push(
            { label: 'Super Admin', value: ROLES.SUPER_ADMIN, isCheck: false },
            {
              label: 'Super Manager',
              value: ROLES.SUPER_MANAGER,
              isCheck: false,
            },
            { label: 'Admin', value: ROLES.ADMIN, isCheck: false },
          );
        }
      } else if (gridKey === 'credits-history') {
        roleStatus.push(
          { label: 'Admin', value: ROLES.ADMIN, isCheck: false },
          { label: 'Manager', value: ROLES.MANAGER, isCheck: false },
          { label: 'Member', value: ROLES.MEMBER, isCheck: false },
        );
      } else if (gridKey === 'super-manager') {
        if (superAdminOrSuperManager) {
          roleStatus.push(
            { label: 'Sales Admin', value: ROLES.SALES_ADMIN, isCheck: false },
            { label: 'Sales', value: ROLES.SALES, isCheck: false },
          );
        } else if (loginRole === ROLES.SALES_ADMIN) {
          roleStatus.push(
            { label: 'Sales Admin', value: ROLES.SALES_ADMIN, isCheck: false },
            { label: 'Sales', value: ROLES.SALES, isCheck: false },
          );
        } else {
          roleStatus.push({
            label: 'Sales',
            value: ROLES.SALES,
            isCheck: false,
          });
        }
      } else {
        roleStatus.push(
          { label: 'Manager', value: ROLES.MANAGER, isCheck: false },
          { label: 'Member', value: ROLES.MEMBER, isCheck: false },
        );
        if (superAdminOrSuperManager) {
          roleStatus.push({
            label: 'Admin',
            value: ROLES.ADMIN,
            isCheck: false,
          });
        }
      }
      activeStatus.push(...roleStatus);
    } else if (FilterType?.field === 'document_type') {
      activeStatus.push(
        {
          label: 'Bank Statement',
          value: MODELS.BANK_STATEMENT,
          isCheck: false,
        },
        {
          label: 'Credit Card Statement',
          value: MODELS.CREDIT_CARD_STATEMENT,
          isCheck: false,
        },
        { label: 'Sales', value: MODELS.SALES },
        { label: 'Direct Expenses', value: MODELS.INVOICE, isCheck: false },
        { label: 'Indirect Expenses', value: MODELS.RECEIPT, isCheck: false },
      );
    }
    if (
      Object.keys(filterDropDownList).length > 0 &&
      filterDropDownList[FilterType?.field] &&
      filterDropDownList[FilterType?.field].length > 0 &&
      activeStatus.length > 0
    ) {
      const updatedData = activeStatus.map((item) => {
        if (
          isValueInArray(filterDropDownList[FilterType?.field], item.value) &&
          !item.isCheck
        ) {
          return { ...item, isCheck: true }; // Update isCheck to true
        }
        return item; // Return the item as is if no update needed
      });
      setDropDownList(updatedData);
    } else {
      setDropDownList(activeStatus);
    }
  };
  useEffect(() => {
    if (FilterType && FilterType?.type === 'checkBox') {
      getDropDownList();
    } else if (FilterType && FilterType?.type === 'text') {
      if (
        Object.keys(filterDropDownList).length > 0 &&
        filterDropDownList[FilterType?.field] &&
        Object.keys(filterDropDownList[FilterType?.field]).length > 0
      ) {
        setTextFieldFilter(filterDropDownList[FilterType?.field]);
      } else {
        setTextFieldFilter({
          filter: '',
          filterType: 'text',
          type: 'contains',
        });
      }
    } else if (FilterType && FilterType?.type === 'date') {
      if (
        Object.keys(filterDropDownList).length > 0 &&
        filterDropDownList[FilterType?.field] &&
        Object.keys(filterDropDownList[FilterType?.field]).length > 0
      ) {
        setDateFilter(filterDropDownList[FilterType?.field]);
      } else {
        setDateFilter({
          dateFrom: null,
          dateTo: null,
          filterType: 'date',
          type: 'inRange',
        });
      }
    }
  }, [FilterType]);
  const handleCheck = (e, index) => {
    const { checked } = e.target;
    const updatedList = dropDownList.map((item, idx) =>
      idx === index ? { ...item, isCheck: checked } : item,
    );

    // Update state with the new array
    setDropDownList(updatedList);
  };
  const handleTextChange = (e) => {
    const { value } = e.target;
    setTextFieldFilter((prev) => ({
      ...prev,
      filter: value,
    }));
  };
  useEffect(() => {
    if (FilterType?.type === 'text') {
      setFilterDropdownList((prevObject) => {
        const updatedObject = { ...prevObject };
        if (textFieldFilter.filter === '') {
          if (updatedObject[FilterType?.field]) {
            delete updatedObject[FilterType?.field];
          }
        } else {
          updatedObject[FilterType?.field] = textFieldFilter;
        }
        return updatedObject;
      });
    }
  }, [textFieldFilter]);
  useEffect(() => {
    // Filter the data to find checked items
    if (FilterType?.type === 'checkBox') {
      const checkedValues = dropDownList.filter((item) => item.isCheck);

      setFilterDropdownList((prevData) => {
        // Update newData based on the current checkedValues
        const updatedData = { ...prevData };

        if (checkedValues.length > 0) {
          updatedData[FilterType?.field] = checkedValues;
        } else if (updatedData[FilterType?.field]) {
          delete updatedData[FilterType?.field];
        }

        // If only one key exists in newData and its length is 0, return an empty object
        if (updatedData[FilterType?.field]) {
          if (
            Object.keys(updatedData).length === 1 &&
            !updatedData[FilterType?.field]?.length
          ) {
            return {};
          }
        }

        return updatedData;
      });
    }
  }, [dropDownList]);

  const handleStartDateChange = (newValue) => {
    if (newValue) {
      const datePart = dayjs(newValue.$d);
      const formattedDatePart = datePart.format('YYYY-MM-DD HH:mm:ss');
      setDateFilter((prev) => ({
        ...prev,
        dateFrom: formattedDatePart,
        dateTo:
          prev.dateTo && new Date(prev.dateTo) < new Date(newValue)
            ? null
            : prev.dateTo,
      }));
    }
  };

  const handleEndDateChange = (newValue) => {
    if (newValue) {
      const datePart = dayjs(newValue.$d);
      const formattedDatePart = datePart.format('YYYY-MM-DD HH:mm:ss');
      setDateFilter((prev) => ({
        ...prev,
        dateTo: formattedDatePart,
      }));
    }
  };
  const handleClearStartDate = () => {
    setDateFilter((prev) => ({
      ...prev,
      dateFrom: null, // Clear the start date
    }));
  };

  const handleClearEndDate = () => {
    setDateFilter((prev) => ({
      ...prev,
      dateTo: null, // Clear the end date
    }));
  };
  const disableDatesForStartDate = (date) => {
    if (dateFilter?.dateTo) {
      return date.isAfter(dayjs(dateFilter.dateTo), 'day');
    }
    return false;
  };

  const disableDatesForEndDate = (date) => {
    if (dateFilter?.dateFrom) {
      return date.isBefore(dayjs(dateFilter.dateFrom), 'day');
    }
    return false;
  };
  useEffect(() => {
    if (FilterType?.type === 'date') {
      setFilterDropdownList((prevObject) => {
        const updatedObject = { ...prevObject };
        if (dateFilter?.dateFrom === null || dateFilter?.dateTo === null) {
          if (updatedObject[FilterType?.field]) {
            delete updatedObject[FilterType?.field];
          }
        } else {
          updatedObject[FilterType?.field] = {
            ...dateFilter,
            dateFrom: dateFilter?.dateFrom,
            dateTo: dateFilter?.dateTo,
          };
        }
        return updatedObject;
      });
    }
  }, [dateFilter]);
  const renderFilterOption = () => {
    if (FilterType) {
      if (FilterType?.type === 'checkBox') {
        return (
          <Box
            sx={{ mb: 3 }}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {dropDownList.map((option, index) => (
              <FormControlLabel
                key={`${index + 1}`}
                control={
                  <Checkbox
                    checked={option.isCheck}
                    onChange={(e) => {
                      handleCheck(e, index);
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        );
      }
      if (FilterType?.type === 'text') {
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <FilterTextField
              fullWidth
              size="small"
              value={textFieldFilter.filter}
              onKeyDown={(event) => {
                if (event.key.toLowerCase() === 'f') {
                  event.stopPropagation();
                }
              }}
              onChange={(e) => {
                handleTextChange(e);
              }}
            />
          </Box>
        );
      }
      if (FilterType?.type === 'date') {
        return (
          <Box
            sx={{
              height: '100%',
              paddingRight: '15px',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack
                direction="column"
                spacing={2}
                sx={{ width: '100%', paddingTop: '10px' }}
              >
                <DatePicker
                  label="Start Date"
                  value={
                    dateFilter?.dateFrom ? dayjs(dateFilter.dateFrom) : null
                  }
                  onChange={handleStartDateChange}
                  shouldDisableDate={disableDatesForStartDate}
                  sx={{ minWidth: '160px' }}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: handleClearStartDate,
                      size: 'small',
                      sx: { width: '100%' },
                      error: false,
                    },
                    textField: {
                      inputProps: {
                        readOnly: true,
                      },
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={dateFilter?.dateTo ? dayjs(dateFilter.dateTo) : null}
                  onChange={handleEndDateChange}
                  shouldDisableDate={disableDatesForEndDate}
                  sx={{ minWidth: '160px' }}
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: handleClearEndDate,
                      size: 'small',
                      sx: { width: '100%' },
                      error: false,
                    },
                    textField: {
                      inputProps: {
                        readOnly: true,
                      },
                    },
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>
        );
      }
    }
  };
  return (
    <CustomFilterBox
      sx={{
        '&::-webkit-scrollbar': {
          width: '0.4em',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888', // Scrollbar thumb color
          borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#555', // Thumb hover color
          borderRadius: '5px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent', // Track color
          borderRadius: '5px',
        },
        scrollbarWidth: 'thin', // For Firefox
        scrollbarColor: '#888 transparent', // For Firefox
      }}
    >
      {renderFilterOption()}
    </CustomFilterBox>
  );
});

CustomFilter.propTypes = {
  FilterType: PropTypes.PropTypes.shape({
    field: PropTypes.string,
    type: PropTypes.string,
    headerName: PropTypes.string,
  }).isRequired,
  gridKey: PropTypes.string.isRequired,
  setFilterDropdownList: PropTypes.func.isRequired,
  filterDropDownList: PropTypes.shape({}).isRequired,
};
export default CustomFilter;
